"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@/lib/utils";
import { Tooltip as MantineTooltip, type TooltipProps as MantineTooltipProps, type TooltipStylesNames } from "@mantine/core";
import classes from "./tooltip.module.css";
type TooltipTheme = "default" | "warning" | "pending";
const THEME_TOOLTIP_CLASSES: Record<TooltipTheme, string | undefined> = {
  default: undefined,
  warning: classes.tooltipWarning,
  pending: classes.tooltipPending
};
type TooltipOwnProps = {
  /** The element that triggers the tooltip on hover */
  children: React.ReactNode;
  /** Tooltip content */
  content: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  sideOffset?: number;
  theme?: TooltipTheme;
  className?: string;
  classNames?: Partial<Record<TooltipStylesNames, string>>;
};
export type TooltipProps = Omit<MantineTooltipProps, "label" | "content" | "position" | "offset" | "children" | "classNames" | "withArrow"> & TooltipOwnProps;

/**
 * Design-system tooltip (Figma StreamHub UI Kit).
 * Built on Mantine `Tooltip` with styles in `tooltip.module.css`.
 */
export function Tooltip(t0) {
  const $ = _c(27);
  let children;
  let className;
  let classNames;
  let content;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      children,
      content,
      side: t1,
      sideOffset: t2,
      theme: t3,
      className,
      classNames,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = classNames;
    $[4] = content;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    children = $[1];
    className = $[2];
    classNames = $[3];
    content = $[4];
    props = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
  }
  const side = t1 === undefined ? "bottom" : t1;
  const sideOffset = t2 === undefined ? 8 : t2;
  const theme = t3 === undefined ? "default" : t3;
  const isThemed = theme !== "default";
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      hover: true,
      focus: true,
      touch: true
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = THEME_TOOLTIP_CLASSES[theme];
  const t6 = classNames?.tooltip;
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = cn(classes.tooltip, t5, t6);
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = isThemed && classes.arrowHidden;
  const t9 = classNames?.arrow;
  let t10;
  if ($[13] !== t8 || $[14] !== t9) {
    t10 = cn(classes.arrow, t8, t9);
    $[13] = t8;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t7) {
    t11 = {
      tooltip: t7,
      arrow: t10
    };
    $[16] = t10;
    $[17] = t7;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== children || $[20] !== className || $[21] !== content || $[22] !== props || $[23] !== side || $[24] !== sideOffset || $[25] !== t11) {
    t12 = <MantineTooltip data-slot="tooltip" unstyled={true} label={content} position={side} offset={sideOffset} withArrow={true} arrowSize={12} multiline={true} w="max-content" maw={360} floatingStrategy="fixed" events={t4} className={className} classNames={t11} {...props}>{children}</MantineTooltip>;
    $[19] = children;
    $[20] = className;
    $[21] = content;
    $[22] = props;
    $[23] = side;
    $[24] = sideOffset;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}